import { ButtonGroup, Form, Button, Row, Col } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  agGridOptionsSingle,
  agGridTheme,
  civilRoles,
  customStyles,
  elecRoles,
  generalRoles,
  MandatoryExplanation,
  MandatoryFieldIndicator,
  mechRoles,
  processRoles,
  structuralRoles,
} from "../../helpers/constants/Constants";
import drawingSetWIP from "../../assets/images/drawingSetWIP.png";
import drawingSetS1 from "../../assets/images/drawingSetS1.png";
import drawingSetS4 from "../../assets/images/drawingSetS4.png";
import drawingSetPublished from "../../assets/images/drawingSetPublished.png";
import {
  addProjectAssetTaskReview,
  getProjectAssetTasks,
} from "../../helpers/axios/projectsAxios";

const DrawingSetModal = ({ show, onHide, selectedDrawingSet }) => {
  const {
    allDocs,
    userDetails,
    regionalStatuses,
    allProjectUsers,
    cloudFiles,
    selectedProject,
  } = useSelector((state) => state.globalReducer);
  const [rowData, setRowData] = useState([]);
  const [statusModal, setStatusModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [submissionData, setSubmissionData] = useState();
  const [checkerRoles, setCheckerRoles] = useState([]);
  const [primaryDiscipline, setPrimaryDiscipline] = useState();
  const [gridPageSizeMode, setGridPageSizeMode] = useState("auto");

  const fetchDrawingSetData = useCallback(
    async (drawingSet) => {
      const mviewDocs = await getProjectAssetTasks(selectedProject[0].id);
      const linkedDocs = [];
      drawingSet.linkedFiles.forEach((file) => {
        const docData = mviewDocs.find(
          (doc) => doc.docNumber === file.modelName.split(".")[0],
        );
        if (docData) {
          linkedDocs.push(docData);
        }
      });
      setRowData(linkedDocs);
    },
    [selectedProject],
  );

  useEffect(() => {
    if (show) {
      fetchDrawingSetData(selectedDrawingSet);
      setPrimaryDiscipline(
        selectedDrawingSet?.drawingSet?.attributes?.displayName
          .split(".")[0]
          .split(" - ")[2],
      );
    }
  }, [fetchDrawingSetData, selectedDrawingSet, show]);

  /////////// AG-GRID STARTING //////////
  const [gridApi, setGridApi] = useState();
  const gridRef = useRef(null);

  const fitGridColumns = useCallback(
    (apiInput) => {
      const api = apiInput || gridApi;
      if (!api) return;

      api.sizeColumnsToFit();
    },
    [gridApi],
  );

  const applyGridPageSizeMode = useCallback(
    (apiInput) => {
      const api = apiInput || gridApi;
      if (!api) return;

      const allRowCount = Math.max(rowData?.length || 0, 1);

      if (gridPageSizeMode === "all") {
        api.setGridOption("paginationAutoPageSize", false);
        api.setGridOption("paginationPageSize", allRowCount);
      } else if (gridPageSizeMode === "auto") {
        api.setGridOption("paginationAutoPageSize", true);
      } else {
        const pageSize = Number(gridPageSizeMode) || 25;
        api.setGridOption("paginationAutoPageSize", false);
        api.setGridOption("paginationPageSize", pageSize);
      }

      api.paginationGoToFirstPage();
    },
    [gridApi, gridPageSizeMode, rowData],
  );

  const gridReady = (params) => {
    setGridApi(params.api);
    requestAnimationFrame(() => {
      fitGridColumns(params.api);
      applyGridPageSizeMode(params.api);
    });
  };

  useEffect(() => {
    applyGridPageSizeMode();
    fitGridColumns();
  }, [applyGridPageSizeMode, fitGridColumns]);

  const [columnDefs] = useState([
    { field: "id", hide: true },
    { field: "publishedRevision", hide: true },
    { field: "sharedRevision", hide: true },
    { field: "versions", hide: true },
    {
      field: "processArea",
      headerName: "Regional Designation",
    },
    { field: "mwhAssetName", headerName: "MWH Asset" },

    {
      field: "task",
      headerName: "Title",
    },

    {
      field: "docNumber",
      headerName: "Document Number",
    },
    {
      field: "discipline",
    },

    {
      field: "status",
    },
    {
      headerName: "Revision",
      valueGetter: (p) => {
        const currentStatus = regionalStatuses.find(
          (s) => s.code === p.data.status,
        );
        if (currentStatus?.published) {
          return p.data.publishedRevision;
        } else {
          return p.data.sharedRevision;
        }
      },
    },
  ]);

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agSetColumnFilter",
    }),
    [],
  );

  const selectionChanged = () => {
    const tempSelectedFiles = gridApi.getSelectedRows();
    setSelectedFiles(tempSelectedFiles);
    assignCheckerRoles(tempSelectedFiles[0]?.discipline); //THIS CREATING AN ETERNAL LOOP NEED TO LOOK AT IT.
  };
  /////////// AG-GRID ENDING //////////

  const isAuthorised = () => {
    let authorized = false;
    switch (selectedFiles[0]?.discipline) {
      case "Civil":
      case "Civil Engineer":
        if (civilRoles.includes(userDetails?.role)) {
          authorized = true;
        }
        break;
      case "Mechanical":
        if (mechRoles.includes(userDetails?.role)) {
          authorized = true;
        }
        break;
      case "Electrical":
      case "EICA":
        if (elecRoles.includes(userDetails?.role)) {
          authorized = true;
        }
        break;
      case "Process":
      case "Process Engineering":
        if (processRoles.includes(userDetails?.role)) {
          authorized = true;
        }
        break;
      case "Structural":
        if (structuralRoles.includes(userDetails?.role)) {
          authorized = true;
        }
        break;
      case "General":
      case "Multi-disciplinary":
        if (generalRoles.includes(userDetails?.role)) {
          authorized = true;
        }
        break;
      default:
        authorized = false;
        break;
    }

    return authorized;
  };

  const assignCheckerRoles = (discipline) => {
    switch (discipline) {
      case "Civil":
      case "Civil Engineer":
        if (civilRoles.includes(userDetails?.role)) {
          setCheckerRoles([
            "Civil Checker",
            "Civil Reviewer",
            "Civil Approver",
            "Design Manager",
            "Admin",
          ]);
        } else {
          setCheckerRoles([]);
        }
        break;
      case "Mechanical":
        if (mechRoles.includes(userDetails?.role)) {
          setCheckerRoles([
            "Mechanical Checker",
            "Mechanical Reviewer",
            "Mechanical Approver",
            "Design Manager",
            "Admin",
          ]);
        } else {
          setCheckerRoles([]);
        }
        break;
      case "Electrical":
      case "EICA":
        if (elecRoles.includes(userDetails?.role)) {
          setCheckerRoles([
            "Electrical Checker",
            "Electrical Reviewer",
            "Electrical Approver",
            "Design Manager",
            "Admin",
          ]);
        } else {
          setCheckerRoles([]);
        }
        break;
      case "Process":
      case "Process Engineering":
        if (processRoles.includes(userDetails?.role)) {
          setCheckerRoles([
            "Process Checker",
            "Process Reviewer",
            "Process Approver",
            "Design Manager",
            "Admin",
          ]);
        } else {
          setCheckerRoles([]);
        }
        break;
      case "Structural":
        if (structuralRoles.includes(userDetails?.role)) {
          setCheckerRoles([
            "Structural Checker",
            "Structural Reviewer",
            "Structural Approver",
            "Design Manager",
            "Admin",
          ]);
        } else {
          setCheckerRoles([]);
        }
        break;
      case "General":
      case "Multi-disciplinary":
        if (generalRoles.includes(userDetails?.role)) {
          setCheckerRoles([
            "Civil Checker",
            "Civil Reviewer",
            "Civil Approver",
            "Mechanical Checker",
            "Mechanical Reviewer",
            "Mechanical Approver",
            "Electrical Checker",
            "Electrical Reviewer",
            "Electrical Approver",
            "Structural Checker",
            "Structural Reviewer",
            "Structural Approver",
            "Process Checker",
            "Process Reviewer",
            "Process Approver",
            "Design Manager",
            "Admin",
          ]);
        } else {
          setCheckerRoles([]);
        }
        break;

      default:
        setCheckerRoles([]);
        break;
    }
  };

  const requestAssetReview = async () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    let mm = today.getMonth() + 1; // Months start at 0!
    let dd = today.getDate();

    if (dd < 10) dd = "0" + dd;
    if (mm < 10) mm = "0" + mm;

    const formattedToday = dd + "/" + mm + "/" + yyyy;

    const tempCloudFile = cloudFiles.find((o) =>
      o?.attributes?.displayName?.includes(selectedFiles[0]?.docNumber),
    );

    const tempSelectedModel = {
      id: tempCloudFile?.relationships?.derivatives?.data?.id,
      documentName: tempCloudFile?.attributes.name,
      storageLocation: null, //tempCloudFile?.relationships?.storage?.meta?.link?.href,
      task: selectedFiles[0]?.task,
      status: selectedFiles[0]?.status,
      sharedRevision: selectedFiles[0]?.sharedRevision,
      publishedRevision: selectedFiles[0]?.publishedRevision,
      projectAssetTaskId: selectedFiles[0]?.id,
      selectedVersion: selectedFiles[0]?.versions.find(
        (v) => v.current === true,
      )?.id,
      asset: selectedFiles[0]?.processArea,
      versions: selectedFiles[0]?.versions,
      visible: true,
      discipline: selectedFiles[0]?.discipline,
    };

    const reviewData = {
      ...submissionData,
      proposedStatus: submissionData.proposedStatus.data.code,
      submittedBy: userDetails?.email,
      submittedDate: formattedToday,
      projectAssetTaskId: selectedFiles[0]?.id,
      discipline: selectedFiles[0]?.discipline,
      selectedModels: [tempSelectedModel],
    };
    const newReview = await addProjectAssetTaskReview(reviewData);
    setSubmissionData();
    window.alert(newReview.data.message);
    hideStatusModal();
  };

  const checkDrawingSetStatus = () => {
    const primaryDisciplineCheck = [];
    const secondaryDisciplineCheck = [];
    const publishedDisciplineCheck = [];
    if (regionalStatuses?.length === 0) {
      return "WIP";
    }
    regionalStatuses?.forEach((status) => {
      if (status.published) {
        publishedDisciplineCheck.push(status.code);
      }
      if (status.primary) {
        primaryDisciplineCheck.push(status.code);
      }
      if (status.secondary) {
        secondaryDisciplineCheck.push(status.code);
      }
    });

    if (
      !selectedDrawingSet?.linkedFiles ||
      !Array.isArray(selectedDrawingSet.linkedFiles)
    ) {
      return "WIP";
    }

    const primaryDisciplineStatuses = [];
    const otherModelsStatuses = [];

    selectedDrawingSet.linkedFiles.forEach((file) => {
      const docNumber = file.modelName.split(".")[0];
      const doc = allDocs.find((d) => d.docNumber === docNumber);
      if (doc?.discipline === primaryDiscipline) {
        primaryDisciplineStatuses.push(doc?.status);
      } else {
        otherModelsStatuses.push(doc?.status);
      }
    });

    if (
      primaryDisciplineStatuses.length === 0 &&
      otherModelsStatuses.length === 0
    ) {
      return "WIP";
    }

    var allPublished =
      primaryDisciplineStatuses.every((status) => status === "Published") &&
      otherModelsStatuses.every((status) =>
        publishedDisciplineCheck.includes(status),
      );

    var allPrimaryPrimaryStatuses = primaryDisciplineStatuses.every((status) =>
      primaryDisciplineCheck.includes(status),
    );
    var allOtherSecondaryStatuses = otherModelsStatuses.every((status) =>
      secondaryDisciplineCheck.includes(status),
    );

    var anyWip =
      primaryDisciplineStatuses.includes("S0") ||
      otherModelsStatuses.includes("S0");

    if (allPublished) {
      return "Published";
    }
    if (allPrimaryPrimaryStatuses && allOtherSecondaryStatuses) {
      return "S4";
    }
    if (!anyWip && allOtherSecondaryStatuses) {
      return "S1";
    }
    if (anyWip) {
      return "WIP";
    }
    // If no statuses match the above conditions, return WIP
    return "WIP";
  };

  const drawingSetImage = () => {
    let image = drawingSetWIP;

    const status = checkDrawingSetStatus();

    switch (status) {
      case "WIP":
        image = drawingSetWIP;
        break;
      case "S1":
        image = drawingSetS1;
        break;
      case "S4":
        image = drawingSetS4;
        break;
      case "Published":
        image = drawingSetPublished;
        break;
      default:
        image = drawingSetWIP;
        break;
    }

    return (
      <div className="text-center drawing-set-info-modal__status-image">
        <img src={image} alt={status} />
      </div>
    );
  };

  const hideStatusModal = () => {
    setStatusModal(false);
    setSubmissionData();
  };

  const modalSelectStyles = {
    ...customStyles,
    control: (provided, state) => ({
      ...provided,
      fontSize: "12px",
      background:
        "linear-gradient(120deg, rgba(58,58,58,0.34) 0%, rgba(0,93,133,0.42) 100%)",
      borderColor: state.isFocused
        ? "rgba(147,213,0,0.72)"
        : "rgba(242,242,242,0.34)",
      minHeight: "34px",
      height: "34px",
      borderRadius: "999px",
      boxShadow: state.isFocused
        ? "0 0 0 2px rgba(147,213,0,0.24), 0 8px 18px rgba(58,58,58,0.22)"
        : "0 8px 18px rgba(58,58,58,0.2)",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "rgba(242,242,242,0.96)",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "rgba(242,242,242,0.7)",
    }),
    input: (provided) => ({
      ...provided,
      color: "rgba(242,242,242,0.96)",
    }),
    option: (provided, state) => ({
      ...provided,
      color: "rgba(242,242,242,0.95)",
      background: state.isSelected
        ? "linear-gradient(120deg, rgba(147,213,0,0.42), rgba(156,197,202,0.28))"
        : state.isFocused
          ? "linear-gradient(120deg, rgba(147,213,0,0.26), rgba(156,197,202,0.2))"
          : "rgba(0,0,0,0)",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "12px",
      border: "1px solid rgba(242,242,242,0.24)",
      background:
        "linear-gradient(165deg, rgba(58,58,58,0.96) 0%, rgba(0,93,133,0.92) 100%)",
      boxShadow: "0 14px 28px rgba(58,58,58,0.28)",
      overflow: "hidden",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "rgba(156,197,202,0.95)",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        dialogClassName="project-info-modal drawing-set-info-modal"
        center="true"
      >
        <Modal.Header className="project-info-modal__header">
          <Modal.Title>Selected drawing set information</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={onHide}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body drawing-set-info-modal__body">
          <>
            <div className="row project-info-modal__actions-row">
              <div
                className="col text-center project-info-modal__actions"
                data-bs-toggle="tooltip"
              >
                <span
                  data-bs-toggle="tooltip"
                  title={
                    !isAuthorised()
                      ? "You can only change the status of files that you are authorised to do so via your role on this project."
                      : ""
                  }
                >
                  <button
                    type="button"
                    className="btn mwh-btn-primary btn-sm"
                    onClick={(e) => {
                      setStatusModal(true);
                    }}
                    disabled={!isAuthorised()}
                  >
                    Change Status
                  </button>
                </span>
              </div>
            </div>
            <div className="project-info-modal__grid-wrap drawing-set-info-modal__grid-wrap">
              <div className="project-config-grid-table">
                <AgGridReact
                  theme={agGridTheme}
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  paginationAutoPageSize={gridPageSizeMode === "auto"}
                  paginationPageSizeSelector={false}
                  onGridReady={gridReady}
                  onGridSizeChanged={(params) => {
                    fitGridColumns(params.api);
                    if (gridPageSizeMode === "auto") {
                      applyGridPageSizeMode(params.api);
                    }
                  }}
                  onFirstDataRendered={(params) => {
                    fitGridColumns(params.api);
                    applyGridPageSizeMode(params.api);
                  }}
                  onSelectionChanged={selectionChanged}
                  gridOptions={agGridOptionsSingle}
                />
                <div className="project-config-grid-rows-view">
                  <Form.Label className="mb-0 me-2">Page size</Form.Label>
                  <Form.Select
                    size="sm"
                    value={gridPageSizeMode}
                    onChange={(e) => {
                      setGridPageSizeMode(e.target.value);
                    }}
                    style={{ maxWidth: "180px" }}
                  >
                    <option value="auto">Auto</option>
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                    <option value="all">All (scroll)</option>
                  </Form.Select>
                </div>
              </div>
            </div>
            <div>{drawingSetImage()}</div>
          </>
        </Modal.Body>
      </Modal>
      <Modal
        show={statusModal}
        onHide={hideStatusModal}
        dialogClassName="project-info-modal project-status-modal"
        center="true"
      >
        <Modal.Header className="project-info-modal__header">
          <Modal.Title>Status Update Request</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={hideStatusModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body project-status-modal__body">
          <div className="row project-status-modal__form">
            <div className="col">
              <Form.Label>
                What is the desired status?
                <MandatoryFieldIndicator />
              </Form.Label>

              <Select
                className="mb-2"
                menuPortalTarget={document.body}
                styles={modalSelectStyles}
                onChange={(e) => {
                  setSubmissionData({
                    ...submissionData,
                    proposedStatus: e,
                  });
                }}
                options={regionalStatuses?.map((status) => {
                  return {
                    label: `${status.code} - ${status.description}`,
                    value: `${status.code} - ${status.description}`,
                    data: status,
                  };
                })}
                value={submissionData?.proposedStatus}
              />
              <Form.Label className="mt-1">
                Who is the checker?
                <MandatoryFieldIndicator />
              </Form.Label>
              <Select
                className="mb-2"
                menuPortalTarget={document.body}
                styles={modalSelectStyles}
                onChange={(e) => {
                  setSubmissionData({
                    ...submissionData,
                    checker: e.value,
                  });
                }}
                options={allProjectUsers
                  .filter((o) => checkerRoles.includes(o.role))
                  .map((user) => {
                    return { label: user.email, value: user.email };
                  })}
                value={{
                  value: submissionData?.checker,
                  label: submissionData?.checker,
                }}
              />
              {/* <Form.Label>Who is the approver?</Form.Label>
              <Select
                className="mb-2"
                menuPortalTarget={document.body}
                styles={customStyles}
                onChange={(e) => {
                  setSubmissionData({
                    ...submissionData,
                    approver: e.value,
                  });
                }}
                options={allProjectUsers
                  .filter((o) => reviewerRoles.includes(o.role))
                  .map((user) => {
                    return { label: user.email, value: user.email };
                  })}
                value={{
                  value: submissionData?.approver,
                  label: submissionData?.approver,
                }}
              /> */}
            </div>
            <div className="col ">
              {/* <Form.Label>???</Form.Label>
              <Form.Control
                type="text"
                // value={selectedMarkup?.markupData.userName}
              /> */}
              <Form.Label className="mt-1">Comments</Form.Label>
              <Form.Control
                className="project-status-modal__textarea"
                rows={4}
                as="textarea"
                onChange={(e) => {
                  setSubmissionData({
                    ...submissionData,
                    comments: {
                      id: 1,
                      user: userDetails.email,
                      date: new Date().toISOString(),
                      comment: e.target.value,
                    },
                  });
                }}
                value={submissionData?.comments?.comment}
              />
            </div>
          </div>
          <Row>
            <Col>
              <ButtonGroup className="project-status-modal__actions">
                <Button
                  variant="outline-success"
                  size="sm"
                  className="project-status-modal__action-btn"
                  onClick={requestAssetReview}
                  disabled={
                    !submissionData?.checker || !submissionData?.proposedStatus
                  }
                >
                  Request check
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <MandatoryExplanation />
        </Modal.Body>
      </Modal>
    </>
  );
};
export default DrawingSetModal;
